import countryLanguage from 'country-language';

/**
 * Convert a language code to a country code using only the country-language library
 * @param {string} languageCode - ISO 639-1 language code (e.g., 'en', 'fr')
 * @param {object} options - Configuration options
 * @param {string[]} options.preferredCountries - List of preferred countries to prioritize
 * @returns {string} ISO 3166-1 alpha-2 country code (e.g., 'US', 'FR')
 */
export const languageToCountry = (languageCode, options = {}) => {
  try {
    const languagePriorities = {
      en: 'US',
      de: 'DE',
      es: 'ES',
      fr: 'FR',
    };

    if (languagePriorities[languageCode]) {
      return languagePriorities[languageCode];
    }

    // Get the language information
    const language = countryLanguage.getLanguage(languageCode);

    if (language && language.countries && language.countries.length > 0) {
      // If we have preferred countries, check if any of them are in the list
      if (options.preferredCountries && options.preferredCountries.length > 0) {
        for (const preferredCountry of options.preferredCountries) {
          const foundCountry = language.countries.find((c) => c.code_2 === preferredCountry);
          if (foundCountry) {
            return foundCountry.code_2;
          }
        }
      }

      // Find countries where this is an official language
      const officialCountries = language.countries.filter((country) => {
        // Check if this language is listed as official for this country
        if (country.languages) {
          return country.languages.some(
            (lang) => lang.iso639_1 === languageCode && lang.official === 'Yes',
          );
        }
        return false;
      });

      // If we found countries where this is an official language, use the first one
      if (officialCountries.length > 0) {
        return officialCountries[0].code_2;
      }

      // Otherwise, just use the first country where the language is spoken
      return language.countries[0].code_2;
    }
  } catch (error) {
    console.warn(`Error looking up country for language code ${languageCode}:`, error);
  }

  // If we couldn't find a mapping, return the language code converted to uppercase
  // This is a reasonable fallback for many language codes (en -> EN, fr -> FR)
  return languageCode.toUpperCase();
};

/**
 * Convert a language code to the lowercase country code expected by
 * react-phone-input-2 (e.g. 'da' -> 'dk', 'en' -> 'us'). Keeps the phone
 * field's flag in sync with the flag shown in the language selector.
 * @param {string} languageCode - ISO 639-1 language code (e.g. 'da', 'en')
 * @param {string} fallback - Country code to use when no language is set
 * @returns {string} Lowercase ISO 3166-1 alpha-2 country code
 */
export const languageToPhoneCountry = (languageCode, fallback = 'dk') => {
  if (!languageCode) {
    return fallback;
  }

  return languageToCountry(languageCode).toLowerCase();
};

/**
 * Get a list of all languages supported by the country-language library
 * @returns {Array} List of language objects
 */
export const getAllLanguages = () => {
  try {
    return countryLanguage.getLanguages();
  } catch (error) {
    console.error('Error getting languages:', error);
    return [];
  }
};

/**
 * Get a list of all countries supported by the country-language library
 * @returns {Array} List of country objects
 */
export const getAllCountries = () => {
  try {
    return countryLanguage.getCountries();
  } catch (error) {
    console.error('Error getting countries:', error);
    return [];
  }
};

/**
 * Get all language codes with their native names
 * @returns {Object} Object with language codes as keys and language names as values
 */
export const getLanguageNames = () => {
  const languages = getAllLanguages();
  const result = {};

  languages.forEach((lang) => {
    if (lang.iso639_1 && lang.name) {
      result[lang.iso639_1] = lang.name;
    }
  });

  return result;
};

/**
 * Get countries where a specific language is spoken
 * @param {string} languageCode - ISO 639-1 language code
 * @returns {Array} List of country objects
 */
export const getCountriesForLanguage = (languageCode) => {
  try {
    const language = countryLanguage.getLanguage(languageCode);
    return language ? language.countries : [];
  } catch (error) {
    console.error(`Error getting countries for language ${languageCode}:`, error);
    return [];
  }
};
